import { Grid, Paper, Typography } from '@mui/material'
import { Formik } from 'formik'
import { sortBy } from 'lodash'
import { observer } from 'mobx-react-lite'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { getErrMsg, toastNeg, toastPos } from '../../common/toastHelper'
import { IEditedSkillInfo, INewSkill, INewSkillInit, ISkills } from '../../interfaces/skills/ISkills'
import { useStore } from '../../Provider'
import { ExitConfirmation } from '../OPcomponents/ExitConfirmation'
import { OPCheckBox } from '../OPcomponents/OPCheckBox'
import { OPMultiline } from '../OPcomponents/OPMultiline'
import { OPSkillsCombobox } from '../OPcomponents/OPSkillsCombobox'
import { newSkillSchema } from '../OPcomponents/ValidationSchemas'
import { OPSkillsButtons } from '../Profile/OPSkillsButtons'

const AddSkill = (): JSX.Element => {
  const { t } = useTranslation()
  const rootStore = useStore()
  const { validatedUser } = rootStore.loginStore
  const { dropdownCategories, showModalHandler, createNewSkill, allParentSkills, allSkills, getCategories } = rootStore.skillStore

  const [similarSkillsFi, setSimilarSkillsFi] = useState<(string | null)[]>([])
  const [similarSkillsEng, setSimilarSkillsEng] = useState<(string | null)[]>([])
  const [duplicateFiInCategory, setDuplicateFiInCategory] = useState(false)
  const [duplicateEngInCategory, setDuplicateEngInCategory] = useState(false)

  // Helper to find similar skills in allSkills
  const findSimilarSkills = (input: string, type: 'eng' | 'fi', opts?: { excludeCategoryId?: number }) => {
    if (!input) return []
    const lowerInput = input.toLowerCase()
    const scoredResults = allSkills
      .map((skill: ISkills) => {
        const { skillNameFi, skillNameEng, skillDescription } = skill.skill
        const name = (type === 'eng' ? skillNameEng : skillNameFi)?.toLowerCase() || ''
        const description = skillDescription?.toLowerCase() || ''
        let score = 0
        if (name === lowerInput) score += 100
        else if (name.startsWith(lowerInput)) score += 75
        else if (name.includes(lowerInput)) score += 50
        if (description.includes(lowerInput)) score += 20
        return { skill: skill.skill, score }
      })
      .filter((item) => item.score > 0)
      .filter((item) => (opts?.excludeCategoryId ? item.skill.categoryId !== opts.excludeCategoryId : true))
    if (scoredResults.length === 0) return []
    const sortedResults = sortBy(scoredResults, [(item) => -item.score])
    return sortedResults.map((item) => (type === 'eng' ? item.skill.skillNameEng : item.skill.skillNameFi)).splice(0, 5) ?? []
  }

  const hasExactDuplicateInCategory = (input: string, type: 'eng' | 'fi', categoryId: number) => {
    if (!input || !categoryId) return false
    const lower = input.trim().toLowerCase()
    return allSkills.some((s: ISkills) => {
      const sameCategory = s.skill.categoryId === categoryId
      if (!sameCategory) return false
      const name = (type === 'eng' ? s.skill.skillNameEng : s.skill.skillNameFi) || ''
      return name.trim().toLowerCase() === lower
    })
  }

  const newSkillInit: INewSkillInit = {
    nameFi: '',
    nameEng: '',
    description: '',
    descriptionEng: '',
    categoryId: 0,
    parentSkillId: 0,
    creatorId: validatedUser,
    personId: validatedUser,
    skillLevel: 0,
    interest: 0,
    comment: '',
    skillId: 0,
    addSkillToUser: false,
    isRequestedByCustomers: false,
  }

  const handleSubmit = async (values: INewSkillInit) => {
    const newSkill: IEditedSkillInfo = {
      nameFi: values.nameFi,
      nameEng: values.nameEng,
      description: values.description,
      descriptionEng: values.descriptionEng,
      categoryId: values.categoryId,
      creatorId: values.creatorId,
      isRequestedByCustomers: values.isRequestedByCustomers,
      parentSkillId: values.parentSkillId,
    }
    const skillToUser: INewSkill = {
      personId: values.personId,
      skillId: values.skillId,
      skillLevel: values.skillLevel,
      interest: values.interest,
      comment: values.comment,
    }

    try {
      await createNewSkill(newSkill, skillToUser, values.addSkillToUser)
      toastPos(t('Toasts.SkillCreate'))
    } catch (error) {
      toastNeg(getErrMsg(error, t) || t('Toasts.SkillCreateError'))
    } finally {
      showModalHandler()
    }
  }

  useEffect(() => {
    getCategories()
  }, [getCategories])

  return (
    <Paper className='profile--paper__skillpadding'>
      <Formik
        initialValues={newSkillInit}
        validateOnMount
        onSubmit={(values) => handleSubmit(values)}
        validationSchema={newSkillSchema}
        enableReinitialize
      >
        {({ isValid, values, setFieldValue, handleChange, isSubmitting, handleSubmit, resetForm, dirty }) => {
          const duplicateFiText = duplicateFiInCategory ? t('Skills.DuplicateInCategory') : undefined
          const duplicateEngText = duplicateEngInCategory ? t('Skills.DuplicateInCategory') : undefined
          return (
            <Grid container rowSpacing={2} columnSpacing={4}>
              <ExitConfirmation when={dirty} />
              <Grid item xs={12} className='projects--skillheader'>
                <span>{t('Skills.NewSkill')}</span>
              </Grid>
              <Grid item xs={12} lg={6}>
                <OPSkillsCombobox
                  name='categoryId'
                  optionList={dropdownCategories}
                  curState={dropdownCategories.find((x) => x.key === values.categoryId)}
                  label={`${t('Skills.Category')} *`}
                  disableClearable={false}
                  onChange={(_e, val) => {
                    const key = val?.key ?? 0
                    setFieldValue('categoryId', key)
                    setDuplicateFiInCategory(hasExactDuplicateInCategory(values.nameFi, 'fi', key))
                    setDuplicateEngInCategory(hasExactDuplicateInCategory(values.nameEng || '', 'eng', key))
                    setSimilarSkillsFi(findSimilarSkills(values.nameFi, 'fi', { excludeCategoryId: key }))
                    setSimilarSkillsEng(findSimilarSkills(values.nameEng || '', 'eng', { excludeCategoryId: key }))
                  }}
                  onDelete={() => {
                    setFieldValue('categoryId', 0)
                  }}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <OPSkillsCombobox
                  name='parentSkillId'
                  optionList={allParentSkills}
                  curState={allParentSkills?.find((x) => x.key === values.parentSkillId)}
                  disableClearable={false}
                  label={t('Skills.ParentSkill')}
                  onChange={(_e, val) => {
                    setFieldValue('parentSkillId', val?.key ?? 0)
                  }}
                  onDelete={() => {
                    setFieldValue('parentSkillId', 0)
                  }}
                />
              </Grid>
              <Grid item xs={6} lg={6}>
                <OPMultiline
                  label={`${t('Skills.SkillFinnishName')} *`}
                  name='nameFi'
                  value={values.nameFi}
                  disabled={!values.categoryId}
                  onChange={(event) => {
                    setFieldValue('nameFi', event.target.value)
                    setDuplicateFiInCategory(hasExactDuplicateInCategory(event.target.value, 'fi', values.categoryId))
                    setSimilarSkillsFi(findSimilarSkills(event.target.value, 'fi', { excludeCategoryId: values.categoryId }))
                  }}
                  error={duplicateFiInCategory}
                  errorText={duplicateFiText}
                  hasCounter
                  autoFocus
                  maxChars={100}
                />
                {!duplicateFiInCategory && values.nameFi && values.categoryId ? (
                  <Typography variant='caption' color='text.secondary'>
                    {similarSkillsFi && similarSkillsFi.length > 0 ? `${t('Skills.SimilarFound')}: ${similarSkillsFi.join(', ')}` : ''}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={6} lg={6}>
                <OPMultiline
                  label={`${t('Skills.SkillEnglishName')} *`}
                  name='nameEng'
                  value={values.nameEng}
                  disabled={!values.categoryId}
                  onChange={(event) => {
                    setFieldValue('nameEng', event.target.value)
                    setDuplicateEngInCategory(hasExactDuplicateInCategory(event.target.value, 'eng', values.categoryId))
                    setSimilarSkillsEng(findSimilarSkills(event.target.value, 'eng', { excludeCategoryId: values.categoryId }))
                  }}
                  error={duplicateEngInCategory}
                  errorText={duplicateEngText}
                  hasCounter
                  maxChars={100}
                />
                {!duplicateEngInCategory && values.nameEng && values.categoryId ? (
                  <Typography variant='caption' color='text.secondary'>
                    {similarSkillsEng && similarSkillsEng.length > 0 ? `${t('Skills.SimilarFound')}: ${similarSkillsEng.join(', ')}` : ''}
                  </Typography>
                ) : null}
              </Grid>
              <Grid item xs={12} lg={6}>
                <OPMultiline
                  label={`${t('Skills.SkillOverview')} *`}
                  maxChars={250}
                  hasCounter
                  minRows={5}
                  value={values.description}
                  name='description'
                  disabled={!values.categoryId}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <OPMultiline
                  label={`${t('Skills.SkillOverviewEng')} *`}
                  maxChars={250}
                  hasCounter
                  minRows={5}
                  value={values.descriptionEng}
                  name='descriptionEng'
                  disabled={!values.categoryId}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item xs={12}>
                <OPCheckBox
                  label={t('Skills.RequestedByCustomers')}
                  value={values.isRequestedByCustomers}
                  onChangeState={(_e, val) => setFieldValue('isRequestedByCustomers', val)}
                />
              </Grid>
              <Grid item xs={12}>
                <OPSkillsButtons
                  noPadding
                  disabled={
                    !isValid ||
                    !values.categoryId || // ensure category is selected to enable submit
                    isSubmitting ||
                    duplicateFiInCategory ||
                    duplicateEngInCategory
                  }
                  bothDisabled={isSubmitting}
                  onClose={showModalHandler}
                  onSubmit={handleSubmit}
                />
              </Grid>
            </Grid>
          )
        }}
      </Formik>
    </Paper>
  )
}
export default observer(AddSkill)
